import { Link } from 'react-router-dom';
import type { CampaignStats } from '@/api/types';
import { EXPERIMENT_STATUS_LABELS, formatPct } from './campaignUi';
import campStyles from './Campaigns.module.css';

type Props = {
  stats: CampaignStats | undefined;
  loading?: boolean;
  compact?: boolean;
};

export function CampaignKpiStrip({ stats, loading }: Props) {
  if (loading) {
    return (
      <div className={campStyles.kpiStrip}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={campStyles.kpiCard}>
            <p className={campStyles.kpiLabel}>…</p>
            <p className={campStyles.kpiValue}>—</p>
          </div>
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const enrolled = stats.enrolled ?? Object.values(stats.byStatus ?? {}).reduce((a, b) => a + b, 0);

  const funnel = stats.funnel;
  const clicks = stats.mail.clicks ?? funnel?.clicks ?? 0;

  return (
    <div className={campStyles.kpiStrip} role="group" aria-label="Campagne KPI's">
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Enrolled</p>
        <p className={campStyles.kpiValue}>{enrolled}</p>
        {funnel ? (
          <p className={campStyles.kpiHint}>
            actief {funnel.active} · reply {funnel.replied} · klaar {funnel.completed}
          </p>
        ) : null}
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Verstuurd</p>
        <p className={campStyles.kpiValue}>{stats.mail.sent}</p>
        <p className={campStyles.kpiHint}>
          mislukt {stats.mail.failed} · bounce {stats.mail.bounce}
        </p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Reply-rate</p>
        <p className={campStyles.kpiValue}>{formatPct(stats.replyRate)}</p>
        <p className={campStyles.kpiHint}>enrollments met reply-stop</p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>CTA-clicks</p>
        <p className={campStyles.kpiValue}>{clicks}</p>
        <p className={campStyles.kpiHint}>link-tracking in templates</p>
      </div>
      <div className={campStyles.kpiCard}>
        <p className={campStyles.kpiLabel}>Sequence-stappen</p>
        <p className={campStyles.kpiValue}>{stats.stepSent?.length ?? 0}</p>
        <p className={campStyles.kpiHint}>
          {stats.stepSent?.length
            ? stats.stepSent.map((s) => `#${s.step_order}: ${s.sent}`).join(' · ')
            : 'Nog geen sends'}
        </p>
      </div>
    </div>
  );
}

export function CampaignStatsPanel({ stats, loading }: Props) {
  if (loading) {
    return (
      <section className={campStyles.panel}>
        <div className={campStyles.panelHead}>
          <h2 className={campStyles.panelTitle}>Prestaties</h2>
        </div>
        <p className={campStyles.panelBodyMuted}>Statistieken laden…</p>
      </section>
    );
  }
  if (!stats) {
    return (
      <section className={campStyles.panel}>
        <div className={campStyles.panelHead}>
          <h2 className={campStyles.panelTitle}>Prestaties</h2>
        </div>
        <p className={campStyles.panelBodyMuted}>
          Nog geen data — start de campagne of wacht op de worker.
        </p>
      </section>
    );
  }

  const analysis = stats.experimentAnalysis ?? stats.stepExperimentAnalysis;
  const stepAb = Boolean(stats.stepExperimentAnalysis && !stats.experimentAnalysis);
  const templates = stats.templateKpi ?? [];

  return (
    <section className={campStyles.panel}>
      <div className={campStyles.panelHead}>
        <h2 className={campStyles.panelTitle}>Prestaties & experimenten</h2>
      </div>
      <div className={campStyles.panelBody}>
        {Object.keys(stats.byStatus ?? {}).length > 0 ? (
          <div className={campStyles.chipRow}>
            {Object.entries(stats.byStatus).map(([k, v]) => (
              <span key={k} className={campStyles.metaChip}>
                {k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        ) : null}

        {stats.stepExperimentVariants?.length ? (
          <div className={campStyles.analysisBlock}>
            <div className={campStyles.analysisHead}>
              <h3 className={campStyles.analysisTitle}>Stap-varianten (detail)</h3>
            </div>
            <p className={campStyles.panelBodyMuted}>
              Positieve reply-rate = geclassificeerd als interested, meeting_request of question (excl.
              review). Vereist reply-classificatie per project.
            </p>
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Variant</th>
                    <th>Verstuurd</th>
                    <th>Reply-rate</th>
                    <th>Positieve reply-rate</th>
                    <th>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.stepExperimentVariants.map((v) => (
                    <tr key={v.variant_id}>
                      <td>
                        <strong>{v.variant_key}</strong>
                        {v.label ? ` — ${v.label}` : ''}
                      </td>
                      <td>{v.sent}</td>
                      <td>{formatPct(v.replyRate, 2)}</td>
                      <td>{formatPct(v.positiveReplyRate ?? 0, 2)}</td>
                      <td>{v.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {analysis ? (
          <div className={campStyles.analysisBlock}>
            <div className={campStyles.analysisHead}>
              <h3 className={campStyles.analysisTitle}>
                {stepAb ? 'Stap A/B' : 'A/B'} — {analysis.primary_metric.replace(/_/g, ' ')}
              </h3>
              <span
                className={[
                  campStyles.analysisBadge,
                  analysis.comparison.status === 'significant'
                    ? campStyles.analysis_significant
                    : analysis.comparison.status === 'collecting'
                      ? campStyles.analysis_collecting
                      : analysis.comparison.status === 'not_significant'
                        ? campStyles.analysis_not_significant
                        : campStyles.analysis_inconclusive,
                ].join(' ')}
              >
                {EXPERIMENT_STATUS_LABELS[analysis.comparison.status] ??
                  analysis.comparison.status}
              </span>
            </div>
            <p className={campStyles.panelBodyMuted}>{analysis.comparison.message}</p>
            <p className={campStyles.panelBodyMuted}>
              Min. {analysis.min_sample_per_variant} sends per variant
              {analysis.comparison.p_value != null
                ? ` · p ≈ ${analysis.comparison.p_value.toFixed(3)}`
                : ''}
              {analysis.comparison.leading_variant_key
                ? ` · voorlopig: variant ${analysis.comparison.leading_variant_key}`
                : ''}
            </p>
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Variant</th>
                    <th>Verstuurd</th>
                    <th>Rate</th>
                    <th>95% CI</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.variants.map((v) => (
                    <tr key={v.variant_id}>
                      <td>
                        <strong>{v.variant_key}</strong>
                        {v.label ? ` — ${v.label}` : ''}
                        {v.guardrail_unsub_warning ? (
                          <span className={campStyles.warnTag}>Unsub</span>
                        ) : null}
                      </td>
                      <td>{v.sent}</td>
                      <td>{formatPct(v.rate, 2)}</td>
                      <td>
                        {v.ci
                          ? `${formatPct(v.ci.low, 1)} – ${formatPct(v.ci.high, 1)}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {templates.length > 0 ? (
          <div className={campStyles.analysisBlock}>
            <div className={campStyles.analysisHead}>
              <h3 className={campStyles.analysisTitle}>Per template</h3>
              <Link to="/app/campagnes/bibliotheek" className={campStyles.panelLink}>
                Bibliotheek →
              </Link>
            </div>
            <p className={campStyles.panelBodyMuted}>
              Sends zijn gekoppeld aan template-id in mail_messages — vergelijk varianten in A/B, iteratie
              in de bibliotheek.
            </p>
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Verstuurd</th>
                    <th>Reply</th>
                    <th>Clicks</th>
                    <th>CI</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={`${t.email_template_id}-${t.template_version}`}>
                      <td>
                        <Link
                          to={`/app/campagnes/bibliotheek?template=${t.email_template_id}`}
                          className={campStyles.catalogLink}
                        >
                          {t.template_name}
                        </Link>{' '}
                        <span className={campStyles.mutedInline}>v{t.template_version}</span>
                      </td>
                      <td>{t.sent}</td>
                      <td>{formatPct(t.reply_rate, 2)}</td>
                      <td>{t.clicks ?? 0}</td>
                      <td>
                        {t.reply_ci
                          ? `${formatPct(t.reply_ci.low, 1)} – ${formatPct(t.reply_ci.high, 1)}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
